import * as React from 'react';
import { ColorScale, BooleanColors, ColumnDisplayOptions, MapColorsKind } from '../../containers_v2/map/colorScale';
import { DarkGreySidely2 } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { PopupMode } from '../popup/model/Model';
import Popup from '../popup/Popup';
import PopupCreation, { ButtonStyle } from '../popup/PopupCreation';
import { CreationPopupContainer } from '../popup/style/CreationFormStyle';
import BooleanColorsEditor from './BooleanColorsEditor';
import ColorScaleEditor from './ColorScaleEditor';

// The colours of a column's values, edited from its row in the settings or
// from its header in a report: a scale for a numeric column, a colour per
// value for a boolean one. The popup hands the column's display options
// back to the caller, which saves them its own way; the options' other
// keys are kept as they are.

/** The display options with this scale set, or without one when it is cleared. */
export function withColorScale<T extends ColumnDisplayOptions>(options: T | undefined, scale: ColorScale | undefined): T {
	const { color_scale: _previous, ...rest } = options ?? ({} as T);
	return (scale ? { ...rest, color_scale: scale } : rest) as T;
}

export function withBooleanColors<T extends ColumnDisplayOptions>(options: T | undefined, colors: BooleanColors | undefined): T {
	const { boolean_colors: _previous, ...rest } = options ?? ({} as T);
	return (colors ? { ...rest, boolean_colors: colors } : rest) as T;
}

export default function MapColorsPopup<T extends ColumnDisplayOptions>(props: {
	isOpen: boolean,
	title: string | undefined,
	kind: MapColorsKind | undefined,
	// the column is a date: its stops are days from today
	date?: boolean,
	options: T | undefined,
	onClose: () => void,
	onSave: (options: T | undefined) => void,
}) {
	const [options, setOptions] = React.useState<T | undefined>(props.options);

	React.useEffect(() => {
		setOptions(props.options);
	}, [props.options, props.isOpen]);

	// whether the column has colours saved, which the delete takes away
	const hasColors = !!(props.options?.color_scale || props.options?.boolean_colors);
	const cleared = () => withBooleanColors(withColorScale(props.options, undefined), undefined);

	return <Popup isOpen={props.isOpen} onClickOut={props.onClose} popupMode={PopupMode.Details} popupStyle={{ minWidth: '440px' }}>
		<PopupCreation
			title={props.title ?? translateToString('color_scale.values')}
			messageButton={translateToString('save')}
			onSubmit={() => {
				props.onSave(options);
				props.onClose();
			}}
			canValidate={JSON.stringify(options ?? null) !== JSON.stringify(props.options ?? null)}
			additionalButton={[{
				title: translateToString('delete'),
				style: ButtonStyle.Error,
				canValidate: hasColors,
				onSubmit: () => {
					// back to the column's historical colours, as if none were ever set
					props.onSave(cleared());
					props.onClose();
				},
			}]}
		>
			<CreationPopupContainer gap='1em' padding='15px 8px'>
				<label style={{ fontSize: '12px', color: DarkGreySidely2, margin: 0 }}>{translateToString('color_scale.values')}</label>
				{props.kind === 'boolean'
					? <BooleanColorsEditor colors={options?.boolean_colors} onChange={colors => setOptions(withBooleanColors(options, colors))} />
					: <ColorScaleEditor scale={options?.color_scale} date={props.date} onChange={scale => setOptions(withColorScale(options, scale))} />}
			</CreationPopupContainer>
		</PopupCreation>
	</Popup>;
}
